import { useDataGroupStore } from "@/gp/DataGroupStore"
import { CompositeId } from "@/rd/CompositeId"
import { useFamilyStore } from "@/rd/FamilyStore"
import { usePersonStore } from "@/rd/PersonStore"
import { useViewFamilyStore } from "@/rd/ViewFamilyStore"
import { useViewPersonStore } from "@/rd/ViewPersonStore"
import { isDefined } from "@/util/TypeScriptUtil"

function getProfileRelationshipIds(viewPersonId: string) {
    const viewPersonStore = useViewPersonStore()
    const viewFamilyStore = useViewFamilyStore()

    const spouseViewFamilies = viewFamilyStore.getLoadedSpouseFamilies(viewPersonId).spouseFamilies
    const parentViewFamilies = viewPersonStore.getLoadedParentFamilies(viewPersonId).parentFamilies

    // HACK: getLoadedDescendants doesn't return the start person's loaded spouse(s), so get manually
    const spouseViewPersons = spouseViewFamilies.map(sf => sf.spouseOf(viewPersonId))
        .map(id => viewPersonStore.getLoadedPerson(id))
        .filter(isDefined)
    const descendantViewPersons = [...spouseViewPersons, ...viewPersonStore.getLoadedDescendants(viewPersonId, 1)]
    const ancestorViewPersons = [...viewPersonStore.getLoadedAncestors(viewPersonId, 1, 1)]
    const viewPersons = [...descendantViewPersons, ...ancestorViewPersons]

    const allViewFamilies = [...spouseViewFamilies, ...parentViewFamilies]
    const allMatchIds = [...allViewFamilies.flatMap(f => f.matchIds), ...viewPersons.flatMap(vp => vp.matchIds)]
    const groupIds = [...new Set(allMatchIds.map(id => CompositeId.getGroupId(id)!))]

    return {
        spouseViewFamilyIds: spouseViewFamilies.map(vf => vf.id!),
        parentViewFamilyIds: parentViewFamilies.map(vf => vf.id!),
        viewPersonIds: viewPersons.map(vp => vp.id!),
        groupIds,
    }
}

export function areProfileRelationshipsLoaded(viewPersonId?: string) {
    if (!viewPersonId)
        return true

    const viewPersonStore = useViewPersonStore()
    const viewFamilyStore = useViewFamilyStore()
    const dataGroupStore = useDataGroupStore()

    // Check base relationships (ancestors/descendants)
    if (!viewPersonStore.areDescendantsLoaded(viewPersonId, 1))
        return false
    if (!viewPersonStore.areAncestorsLoaded(viewPersonId, 1, 1))
        return false

    const ids = getProfileRelationshipIds(viewPersonId)

    // Check spouse family assertions, family matches, person matches, groups loaded
    const allViewFamilyIds = [...ids.spouseViewFamilyIds, ...ids.parentViewFamilyIds]
    return ids.spouseViewFamilyIds.every(id => viewFamilyStore.areAssertionsLoaded(id)) &&
        viewFamilyStore.areMatchesLoaded(allViewFamilyIds) &&
        viewPersonStore.areMatchesLoaded(ids.viewPersonIds) &&
        dataGroupStore.areGroupsLoaded(ids.groupIds)
}

export async function loadProfileRelationshipsAsync(viewPersonId?: string) {
    if (!viewPersonId)
        return

    const viewPersonStore = useViewPersonStore()
    const viewFamilyStore = useViewFamilyStore()
    const familyStore = useFamilyStore()
    const personStore = usePersonStore()
    const dataGroupStore = useDataGroupStore()

    await Promise.all([
        viewPersonStore.ensureDescendantsLoadedAsync(viewPersonId, 1),
        viewPersonStore.ensureAncestorsLoadedAsync([viewPersonId], 1, 1),
    ])

    const ids = getProfileRelationshipIds(viewPersonId)

    await Promise.all(ids.spouseViewFamilyIds.map(id => viewFamilyStore.ensureAssertionsLoadedAsync(id)))

    const viewFamilies = viewFamilyStore.getLoadedFamilyList([...ids.spouseViewFamilyIds, ...ids.parentViewFamilyIds])
    const viewPersons = viewPersonStore.getLoadedPersonList(ids.viewPersonIds)
    const familyMatchIds = viewFamilies.flatMap(vf => vf.matchIds)
    const personMatchIds = viewPersons.flatMap(vp => vp.matchIds)

    await Promise.all([
        familyStore.getFamilyListAsync(familyMatchIds),
        personStore.getPersonListAsync(personMatchIds),
        dataGroupStore.getGroupListAsync(ids.groupIds),
    ])
}
